// Implements: F-MCK-001, F-UIX-023, F-UIX-008, F-UIX-009, F-UIX-010, F-UIX-013
/**
 * Creating a mockup: the form, the scope it is born in and the way to it.
 *
 * The container owns the typed text and the chosen scope so a refused creation
 * leaves both in the form (F-UIX-008). The options come from the seats of the
 * session, like the selector that moves an existing mockup, with the viewer's
 * own personal scope first (owner decision 2026-10-26): the first seat is chosen
 * until the person picks another, and a person with no organization or team seat
 * creates in their personal scope. The server still
 * decides, and its refusal is a notice the person can close, never one that
 * resends: creating is paid, so only the submit button sends. The mockup and its first
 * turn are created in ONE call (7.6): a refusal leaves no mockup, so the first message stays
 * here. The call travels under a turn id reserved here and kept across presses, so a press
 * after a lost answer gets the same mockup back, never a second one; only an id the server
 * says is taken (409) is let go. Success opens the new mockup at once and hands it the turn
 * the server started (OD-7): the mockup page only follows it. A created mockup whose answer
 * carries no id says so and keeps the message, so nobody creates it twice. The optional reference
 * image (F-CNV-012, UX-04) travels with the creation; another image is another intention, so it is
 * sent under a new turn id.
 */

import { useCallback, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { useNavigate } from "react-router";

import type { ApiError } from "../../../shared/api/ApiError";
import { handoffState } from "../../../shared/api/firstTurnHandoff";
import { readReferenceImage } from "../../../shared/api/referenceImage";
import { useSession } from "../../../shared/api/session";
import { useRateLimitWait } from "../../../shared/api/useRateLimitWait";
import { useTranslation } from "../../../shared/i18n";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { RateLimitWait } from "../../../shared/ui/molecules/RateLimitWait";
import { MockupCreateForm } from "../components/MockupCreateForm";
import type { CreateImageRefusal } from "../components/MockupCreateForm";
import { MockupCreateNotice } from "../components/MockupCreateNotice";
import { creatableScopeOptions, defaultScopeOption } from "../components/scopeOptions";
import { useCreateMockup } from "../hooks/useCreateMockup";
import styles from "./MockupCreateContainer.module.css";

/** The reference image attached to the first message: its name, what is sent, and its digest. */
interface AttachedFile {
  readonly name: string;
  readonly base64: string;
  readonly digest: string;
}

/** The reserved idempotency key and the image it was reserved for: another image is a new intention. */
interface Reserved {
  readonly turnId: string;
  readonly digest: string | null;
}

export interface MockupCreateContainerProps {
  /**
   * A chip the page draws for the scope chosen here (the design it will wear). The container only
   * says which scope that is, so it does not depend on the module that knows about designs.
   */
  readonly designChip?: ((scope: { readonly kind: string; readonly id: string } | null) => ReactNode) | undefined;
}

export function MockupCreateContainer({ designChip }: MockupCreateContainerProps = {}): ReactElement {
  const { t } = useTranslation();
  const { state: session } = useSession();
  const create = useCreateMockup();
  const navigate = useNavigate();
  const { push } = useToast();
  const [firstMessage, setFirstMessage] = useState("");
  const [name, setName] = useState("");
  // `null`: nothing chosen yet, so the first seat offered, or the personal scope without seats.
  const [scope, setScope] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<ApiError | null>(null);
  // The idempotency key of the paid call: the same until the server says it is taken.
  const reserved = useRef<Reserved | null>(null);
  // The synchronous lock of the paid call (checklist 13): `pending` only changes after a render, so two
  // presses in one tick would both pass it.
  const inFlight = useRef(false);
  // F-CNV-012: one optional reference image, kept like the text across a refusal. A file still
  // being read when another is chosen (or this one removed) is ignored by its number.
  const [attached, setAttached] = useState<AttachedFile | null>(null);
  const [imageRefusal, setImageRefusal] = useState<CreateImageRefusal | null>(null);
  const choice = useRef(0);
  const selectImage = useCallback((file: File): void => {
    choice.current += 1;
    const mine = choice.current;
    setImageRefusal(null);
    void readReferenceImage(file).then((read) => {
      if (choice.current !== mine) return;
      if (read.kind === "ok") setAttached({ name: file.name, base64: read.base64, digest: read.digest });
      else setImageRefusal(read.reason);
    });
  }, []);
  const removeImage = useCallback((): void => {
    choice.current += 1;
    setAttached(null);
    setImageRefusal(null);
  }, []);
  // UIX.2: a `429` is a wait, not a failure: nothing is sent until it ends, nor resent by itself.
  const waitSeconds = useRateLimitWait(refusal);

  if (session.status !== "authenticated") {
    return <LoadingState label={t("common.state.loading")} />;
  }
  const options = creatableScopeOptions(session.view.scopes, t("scope.personal"));
  const target = options.find((option) => option.value === scope) ?? defaultScopeOption(options);

  const submit = async (): Promise<void> => {
    if (target === undefined || inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setRefusal(null);
    const text = firstMessage.trim();
    const digest = attached?.digest ?? null;
    const turnId =
      reserved.current !== null && reserved.current.digest === digest ? reserved.current.turnId : crypto.randomUUID();
    reserved.current = { turnId, digest };
    const outcome = await create({
      scopeKind: target.kind,
      scopeId: target.id,
      text,
      name: name.trim() === "" ? null : name.trim(),
      turnId,
      ...(attached === null ? {} : { imageBase64: attached.base64 }),
    });
    inFlight.current = false;
    setPending(false);
    if (!outcome.ok) {
      // Taken by somebody else's call: this id can never be ours, so the next press is new.
      if (outcome.error.status === 409) reserved.current = null;
      setRefusal(outcome.error);
    } else if (outcome.mockupId === null) {
      // Created, but the answer says nothing about where: stay, say so, and KEEP the
      // message. Clearing it would lose a first message whose mockup nobody can open.
      push({ tone: "success", message: t("create.created.unknown") });
    } else {
      // The mockup exists and its first turn is started: land on it at once, handing it the
      // turn to follow (OD-7). Without a run named, the page still finds the open turn itself.
      const handoff =
        outcome.generationId === null
          ? undefined
          : handoffState({ mockupId: outcome.mockupId, turnId, generationId: outcome.generationId });
      void navigate(`/lienzo/${encodeURIComponent(outcome.mockupId)}`, { state: handoff });
    }
  };

  return (
    <div className={styles.create}>
      <MockupCreateForm
        firstMessage={firstMessage}
        onFirstMessageChange={setFirstMessage}
        name={name}
        onNameChange={setName}
        scopeOptions={options}
        scope={target?.value ?? ""}
        onScopeChange={setScope}
        onSubmit={() => {
          void submit();
        }}
        pending={pending}
        waiting={waitSeconds !== null}
        designChip={designChip?.(target?.id == null ? null : { kind: target.kind, id: target.id })}
        image={{ name: attached?.name ?? null, refusal: imageRefusal, onSelect: selectImage, onRemove: removeImage }}
      />
      {waitSeconds === null ? null : <RateLimitWait seconds={waitSeconds} />}
      {refusal === null || refusal.status === 429 ? null : (
        <MockupCreateNotice
          message={userMessage(refusal, t("create.failed"))}
          onClose={() => {
            setRefusal(null);
          }}
        />
      )}
    </div>
  );
}
